import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { formatCOP, formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { downloadXlsx } from '@/lib/download';
import { toast } from 'sonner';
import { Wallet, ArrowDownToLine, Clock, TrendingUp, Banknote, FileDown, DollarSign, Receipt, CheckCircle2 } from 'lucide-react';
import { SellerBankAccountSection } from '@/components/SellerBankAccountSection';
import { SellerPayoutsSection } from '@/components/SellerPayoutsSection';

const WD_STATUS = {
  pending: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved: { label: 'Aprobado', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  paid: { label: 'Pagado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rechazado', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

export default function SellerWallet() {
  const [data, setData] = useState(null);
  const [finance, setFinance] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: '', bank_info: '' });
  const [loading, setLoading] = useState(false);

  useSEO({
    title: 'Finanzas | Panel del vendedor',
    description: 'Consulta ventas, ingresos brutos, comisiones, ganancias netas y solicita retiros.',
    canonical: '/vendedor/finanzas',
  });

  const refresh = () => Promise.all([
    api.get('/sellers/me/wallet').then(({ data }) => setData(data)),
    api.get('/finance/summary').then(({ data }) => setFinance(data)).catch(() => {}),
  ]);
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!form.amount || !form.bank_info) {
      toast.error('Completa el monto y datos bancarios');
      return;
    }
    setLoading(true);
    try {
      await api.post('/sellers/me/withdrawals', {
        amount: parseFloat(form.amount),
        bank_info: form.bank_info,
      });
      toast.success('Solicitud de retiro enviada');
      setOpen(false); setForm({ amount: '', bank_info: '' });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  if (!data) return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;
  const b = data.balance;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Vendedor</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Finanzas</h1>

        {/* Fase D — Métricas ampliadas (ingresos, comisiones, ganancias netas) */}
        {finance && (
          <div data-testid="finance-metrics" className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Receipt} label="Ventas totales" value={finance.total_sales} testid="finance-sales" />
            <Stat icon={DollarSign} label="Ingresos brutos" value={formatCOP(finance.gross_amount)} testid="finance-gross" />
            <Stat icon={TrendingUp} label="Comisiones AutoMarket" value={formatCOP(finance.commissions)} testid="finance-commissions" />
            <Stat icon={CheckCircle2} label="Ganancias netas" value={formatCOP(finance.net_earnings)} testid="finance-net" />
          </div>
        )}

        {/* Balance summary (existing) */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Stat icon={Wallet} label="Disponible para retiro" value={formatCOP(b.available_balance)} accent testid="wallet-available" />
          <Stat icon={TrendingUp} label="Ganado (entregados)" value={formatCOP(b.earned)} testid="wallet-earned" />
          <Stat icon={Clock} label="Pendiente (en tránsito)" value={formatCOP(b.pending_earnings)} testid="wallet-pending-earnings" />
          <Stat icon={Banknote} label="Total retirado" value={formatCOP(b.withdrawn_total)} testid="wallet-withdrawn" />
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-slate-600">
            {b.pending_withdrawals > 0 && (
              <>Tienes <strong>{formatCOP(b.pending_withdrawals)}</strong> en solicitudes pendientes/aprobadas.</>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DownloadStatement />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="wallet-request-btn"
                disabled={b.available_balance <= 0}
                className="rounded-none bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" /> Solicitar retiro
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle>Solicitar retiro</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Monto a retirar (COP)</label>
                  <Input
                    data-testid="wallet-amount-input"
                    type="number" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="rounded-none mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Disponible: <strong>{formatCOP(b.available_balance)}</strong>
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Datos bancarios</label>
                  <Textarea
                    data-testid="wallet-bank-input"
                    value={form.bank_info}
                    onChange={(e) => setForm({ ...form, bank_info: e.target.value })}
                    placeholder="Banco, tipo de cuenta, número, titular, cédula"
                    rows={3}
                    className="rounded-none mt-1"
                  />
                </div>
                <Button
                  data-testid="wallet-submit-btn"
                  onClick={submit} disabled={loading}
                  className="w-full rounded-none bg-slate-900 hover:bg-slate-800"
                >
                  {loading ? 'Enviando...' : 'Enviar solicitud'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* History */}
        <div className="mt-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Historial de retiros</div>
          <div className="border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Monto</th>
                  <th className="px-4 py-3">Solicitado</th>
                  <th className="px-4 py-3">Procesado</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.withdrawals.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin solicitudes aún</td></tr>
                )}
                {data.withdrawals.map((w) => {
                  const st = WD_STATUS[w.status] || { label: w.status, cls: 'bg-slate-100' };
                  return (
                    <tr key={w.withdrawal_id} className="row-hover">
                      <td className="px-4 py-3 font-mono text-xs">{w.withdrawal_id}</td>
                      <td className="px-4 py-3 font-bold">{formatCOP(w.amount)}</td>
                      <td className="px-4 py-3 text-slate-500">{formatDate(w.requested_at)}</td>
                      <td className="px-4 py-3 text-slate-500">{w.processed_at ? formatDate(w.processed_at) : '—'}</td>
                      <td className="px-4 py-3"><Badge className={`rounded-none border ${st.cls}`}>{st.label}</Badge></td>
                      <td className="px-4 py-3 text-xs text-slate-600">{w.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* FASE C+ — Timeline de payouts (por pedido) */}
        <SellerPayoutsSection />

        {/* FASE C — Datos bancarios (KYC para payouts automáticos) */}
        <SellerBankAccountSection />
      </div>
    </Layout>
  );
}

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className={`border p-4 ${accent ? 'border-orange-500 bg-orange-50' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-center gap-2">
      <Icon className={`h-4 w-4 ${accent ? 'text-orange-600' : 'text-slate-500'}`} />
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    </div>
    <div className="mt-2 font-display text-2xl font-black text-slate-900">{value}</div>
  </div>
);

const DownloadStatement = () => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const submit = async () => {
    const params = {};
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;
    const period = range.date_from || range.date_to
      ? `${range.date_from || 'inicio'}_${range.date_to || 'hoy'}`
      : 'completo';
    await downloadXlsx('/sellers/me/statement', `estado_cuenta_${period}.xlsx`, params);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="wallet-download-statement-btn" variant="outline" className="rounded-none">
          <FileDown className="mr-2 h-4 w-4" /> Estado de cuenta (Excel)
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-white">
        <DialogHeader><DialogTitle>Estado de cuenta · Excel</DialogTitle></DialogHeader>
        <div className="space-y-3 text-slate-900">
          <p className="text-sm text-slate-600">
            Resumen + ventas detalladas + retiros del período seleccionado. Deja vacío para incluir todo el histórico.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Desde</label>
              <Input type="date" value={range.date_from}
                onChange={(e) => setRange({ ...range, date_from: e.target.value })}
                className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hasta</label>
              <Input type="date" value={range.date_to}
                onChange={(e) => setRange({ ...range, date_to: e.target.value })}
                className="rounded-none mt-1" />
            </div>
          </div>
          <Button onClick={submit} className="w-full rounded-none bg-slate-900 hover:bg-slate-800 text-white">
            Descargar XLSX
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
